<script lang="ts">
  import type { MousetrapInstance } from 'mousetrap';
  import Mousetrap from 'mousetrap';
  import type { ModeName } from 'shared/definitions/musicTheory/scales';
  import type { SolfegeSystemName } from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
  import { onDestroy, onMount } from 'svelte';
  import { getSolfegeSystemsMapForScaleMode } from '../getSolfegeSystemsMapForScaleMode';
  import sizeDiv from '../sizeDiv';

  // import type { ModeName } from '../../uTheory.Notation/scale';
  // import type { SolfegeSystemName } from '../../uTheory.Notation/solfegeSystem';
  import type { ScaleDegree, SolfegePresentationSystem } from '../solfegeDefinitions';

  export let disabled = false;
  export let presentationSystem: SolfegePresentationSystem = 'scaleDegrees';
  export let solfegeSystem: SolfegeSystemName = 'scaleDegrees';
  export let mode: ModeName | 'allMinors';
  export let callback: (scaleDegreeName: ScaleDegree) => any;
  export let restrictToScaleDegrees: ScaleDegree[] = [];

  let mt: MousetrapInstance;
  let emptyWidthDiv: HTMLDivElement;
  let gameBoardDiv: HTMLDivElement;

  let answerButtons: {
    label: string;
    degreeName: ScaleDegree;
    visible: boolean;
    hotKey: string | undefined;
  }[] = [
    {
      label: 'hi',
      degreeName: '3',
      visible: true,
      hotKey: '3',
    },
  ];

  let gridTemplate = 'repeat(2, 1fr) / repeat(4, 1fr)';

  $: solfegeMap = getSolfegeSystemsMapForScaleMode(mode, solfegeSystem);

  $: answerButtons = solfegeMap.scaleDegrees
    .map((degree, i) => {
      let label = degree.displayString;
      let hotKey: string | undefined;

      if (presentationSystem === 'solfege') {
        label = solfegeMap.solfege[i]!;
        hotKey = solfegeMap.solfege[i]![0]!.toLowerCase();
        if (mode === 'allMinors' && (degree.name === 'M6' || degree.name === 'M7')) {
          hotKey = `shift+${hotKey}`;
        }
      }

      if (presentationSystem === 'scaleDegrees') {
        const degreeNumber = /\d/.exec(degree.name)?.[0]!;
        hotKey = degreeNumber;
        if (mode === 'allMinors' && (degree.name === 'M6' || degree.name === 'M7')) {
          hotKey = `shift+${hotKey}`;
        }
      }

      if (presentationSystem === 'handSigns') label = solfegeMap.handSigns[i]!.svgString;
      else if (presentationSystem === 'names') label = solfegeMap.scaleDegreeNames[i]!;
      return {
        label,
        degreeName: degree.name,
        visible: !restrictToScaleDegrees.length || restrictToScaleDegrees.includes(degree.name),
        hotKey,
      };
    })
    .filter((button) => button.visible);

  $: {
    let buttonsPerRow = 4;
    if (presentationSystem === 'names') {
      buttonsPerRow = 2;
    } else if (mode === 'allMinors') {
      buttonsPerRow = 5;
    }

    const useMultipleRows = answerButtons.length >= buttonsPerRow;
    const numberOfRows = Math.ceil(answerButtons.length / buttonsPerRow);
    const numberOfColumns = useMultipleRows ? buttonsPerRow : answerButtons.length;

    gridTemplate = `repeat(${numberOfRows}, 1fr) / repeat(${numberOfColumns}, 1fr)`;
  }

  function generateHotKeys() {
    if (!mt) return;
    mt.reset();
    answerButtons
      .filter((button, i) => {
        const hasHotKey = !!button.hotKey;
        const hotKeyExistsAlready = answerButtons
          .slice(0, i)
          .find((b) => b.hotKey === button.hotKey);
        return hasHotKey && !hotKeyExistsAlready;
      })
      .forEach((button) => {
        mt.bind(button.hotKey!, () => callback(button.degreeName));
      });
  }

  onMount(() => {
    mt = new Mousetrap();
    generateHotKeys();
  });

  onDestroy(() => {
    mt?.reset();
  });

  export function resize(height: number): number {
    if (!emptyWidthDiv || !gameBoardDiv) return 0;
    const leftover = sizeDiv(emptyWidthDiv.clientWidth, height, gameBoardDiv, { noWrap: true });
    gameBoardDiv.style.height = 'auto';
    return leftover;
  }
</script>

<div bind:this={emptyWidthDiv} />
<div class="solfege-panel-board" bind:this={gameBoardDiv}>
  <div class="buttons" style="grid-template: {gridTemplate}">
    {#each answerButtons as button}
      <button
        {disabled}
        class="uButton button"
        class:scale-degrees={presentationSystem === 'scaleDegrees'}
        class:accidentals={presentationSystem === 'scaleDegrees'}
        on:click={() => callback(button.degreeName)}>{@html button.label}</button
      >
    {/each}
  </div>
</div>

<style lang="postcss">
  .solfege-panel-board {
    text-align: center;
    font-size: 2em;
    padding: 7px;
  }

  .buttons {
    display: grid;
  }

  .button {
    font-size: 1em;
    @media all and (min-width: 800px) {
      font-size: 2em;
    }
  }

  .scale-degrees {
    line-height: 140%;
  }

  :global(.button > svg) {
    padding: 10px;
  }
</style>
